import React, { useEffect } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { getUser, logout } from '../services/helpers'
import logo from "../asset/images/logo.svg"
import { InterviewLeaveGuardProvider, useInterviewLeaveGuard } from './InterviewLeaveGuardContext'

const RESERVED_JOB_SEEKER_SEGMENTS = new Set([
  'browse',
  'applications',
  'onboarding',
  'profile',
  'verify-email',
])

const isPublicJobSeekerRoute = (pathname) => {
  if (pathname === '/jobs/browse' || pathname.startsWith('/jobs/browse/')) return true
  if (pathname.startsWith('/jobs/verify-email/')) return true
  const match = pathname.match(/^\/jobs\/([^/]+)$/)
  if (match && !RESERVED_JOB_SEEKER_SEGMENTS.has(match[1])) return true
  return false
}

const JobSeekerLayoutContent = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { attemptNavigation } = useInterviewLeaveGuard() || {}
  const user = getUser()
  const isLoggedIn =
    typeof window !== 'undefined' &&
    (sessionStorage.getItem('employme-active') === '1' || !!localStorage.getItem('employmeUser'))

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  useEffect(() => {
    if (isLoggedIn) return
    const path = location.pathname
    if (path === '/jobs' || path === '/jobs/') return
    if (!isPublicJobSeekerRoute(path)) {
      navigate('/login', { replace: true, state: { from: path } })
    }
  }, [isLoggedIn, location.pathname, navigate])

  const isNavActive = (path) => {
    if (path === '/jobs') {
      return location.pathname === '/jobs' || location.pathname === '/jobs/'
    }
    if (path === '/jobs/browse') {
      if (location.pathname === '/jobs/browse' || location.pathname.startsWith('/jobs/browse/')) {
        return true
      }
      const jobDetailMatch = location.pathname.match(/^\/jobs\/([^/]+)$/)
      return jobDetailMatch && !RESERVED_JOB_SEEKER_SEGMENTS.has(jobDetailMatch[1])
    }
    if (path === '/jobs/profile') {
      return location.pathname === '/jobs/profile' || location.pathname.startsWith('/jobs/profile/')
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`)
  }

  const DashboardIcon = ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0 7-7 7 7M5 10v10a1 1 0 001 1h3m10-11 2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  )

  const JobsIcon = ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7h16M4 11h16M4 15h10" />
    </svg>
  )

  const ApplicationsIcon = ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  )

  const ProfileIcon = ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  )

  const LogoutIcon = ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
    </svg>
  )

  const navItems = [
    { path: '/jobs', label: 'Dashboard', shortLabel: 'Home', icon: DashboardIcon },
    { path: '/jobs/browse', label: 'Browse jobs', shortLabel: 'Jobs', icon: JobsIcon },
    { path: '/jobs/applications', label: 'Applications', shortLabel: 'Apps', icon: ApplicationsIcon },
    { path: '/jobs/profile', label: 'Profile', shortLabel: 'Profile', icon: ProfileIcon },
  ]

  const activeItem = navItems.find((item) => isNavActive(item.path))
  const isInterviewRoute = /\/jobs\/applications\/[^/]+\/interview/.test(location.pathname)
  const hideMobileChrome = isInterviewRoute

  const handleLogoClick = (e) => {
    e.preventDefault()
    const go = () => {
      if (isLoggedIn) {
        navigate('/jobs')
        return
      }
      navigate('/')
    }
    if (attemptNavigation) {
      attemptNavigation(go)
    } else {
      go()
    }
  }

  const handleNavClick = (e, path) => {
    e.preventDefault()
    const target = getNavTarget(path)
    if (location.pathname === target || location.pathname.startsWith(`${target}/`)) {
      return
    }
    const go = () => navigate(target)
    if (attemptNavigation) {
      attemptNavigation(go)
    } else {
      go()
    }
  }

  const getNavTarget = (path) => {
    if (isLoggedIn) return path
    if (path === '/jobs/browse') return path
    return '/login'
  }

  const handleLogout = () => {
    logout()
  }

  return (
    <div className="min-h-dvh min-h-screen bg-slate-50">
      <aside className="fixed top-0 left-0 z-50 hidden h-full w-64 bg-white shadow-lg md:block">
        <div className="flex h-full flex-col">
          <div className="border-b border-gray-200 px-6 py-5">
            <Link to={isLoggedIn ? '/jobs' : '/'} onClick={handleLogoClick} className="flex items-center gap-2">
              <img className="h-8 w-8" src={logo} alt="Employ Me logo" />
              <span className="text-xl font-semibold text-gray-900">EmployMeGlobal</span>
            </Link>
          </div>

          <div className="border-b border-gray-200 px-6 py-4">
            {isLoggedIn && user ? (
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-200 text-slate-700">
                  {(user.firstName || user.fullName || user.email || 'J')
                    .toString()
                    .charAt(0)
                    .toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="truncate text-sm font-medium text-gray-900">
                    {user.firstName && user.lastName
                      ? `${user.firstName} ${user.lastName}`
                      : user.fullName || 'Job seeker'}
                  </p>
                  <p className="truncate text-xs text-gray-500">{user.email || ''}</p>
                </div>
              </div>
            ) : (
              <div className="space-y-2 text-sm">
                <p className="text-slate-700 font-medium">Welcome</p>
                <p className="text-xs text-slate-500">
                  Log in or create a free account to track your applications and interviews.
                </p>
                <div className="flex gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="flex-1 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                  >
                    Login
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/signup')}
                    className="flex-1 rounded-md bg-primary-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-600"
                  >
                    Sign up
                  </button>
                </div>
              </div>
            )}
          </div>

          <nav className="flex-1 space-y-1 px-4 py-4">
            {navItems.map((item) => {
              const active = isNavActive(item.path)
              const IconComponent = item.icon
              return (
                <Link
                  key={item.path}
                  to={getNavTarget(item.path)}
                  onClick={(e) => handleNavClick(e, item.path)}
                  className={`flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors ${
                    active ? 'bg-primary-500 text-white' : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <IconComponent className={`h-5 w-5 ${active ? 'text-white' : 'text-slate-500'}`} />
                  <span>{item.label}</span>
                </Link>
              )
            })}
          </nav>

          {isLoggedIn && (
            <div className="border-t border-gray-200 p-4">
              <button
                onClick={handleLogout}
                className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
              >
                <LogoutIcon className="h-5 w-5 text-red-600" />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </aside>

      <div className={`flex min-h-dvh min-h-screen flex-col md:ml-64 md:pb-0 ${hideMobileChrome ? 'pb-0' : 'safe-pb-nav'}`}>
        <header className="sticky top-0 z-30 border-b border-gray-200 bg-white/95 backdrop-blur-sm">
          <div className="flex h-14 items-center justify-between gap-3 px-3 sm:h-16 sm:px-4 md:px-6">
            <Link
              to={isLoggedIn ? '/jobs' : '/'}
              onClick={handleLogoClick}
              className="flex shrink-0 items-center md:hidden"
              aria-label="Job seeker home"
            >
              <img className="h-7 w-7" src={logo} alt="" />
            </Link>
            <h1 className="min-w-0 flex-1 truncate text-center text-base font-semibold leading-snug text-gray-900 sm:text-lg md:text-left">
              {isInterviewRoute ? 'Interview' : activeItem?.label || 'Job seeker'}
            </h1>
            {isLoggedIn ? (
              <button
                type="button"
                onClick={handleLogout}
                className="shrink-0 rounded-lg p-2.5 text-red-600 hover:bg-red-50 md:hidden min-h-[44px] min-w-[44px] inline-flex items-center justify-center"
                aria-label="Log out"
              >
                <LogoutIcon className="h-5 w-5" />
              </button>
            ) : (
              <div className="w-9 shrink-0 md:hidden" aria-hidden="true" />
            )}
            <div className="hidden w-9 md:block" aria-hidden="true" />
          </div>
        </header>

        <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-3 sm:px-5 sm:py-5 md:px-6 md:py-6">
          <Outlet />
        </main>
      </div>

      {!hideMobileChrome && (
      <nav
        className="fixed bottom-0 left-0 right-0 z-40 border-t border-slate-200 bg-white/95 pb-[env(safe-area-inset-bottom,0px)] backdrop-blur-sm md:hidden"
        aria-label="Job seeker navigation"
      >
        <div className="mx-auto flex max-w-lg items-stretch justify-around px-1">
          {navItems.map((item) => {
            const IconComponent = item.icon
            const active = isNavActive(item.path)
            return (
              <Link
                key={item.path}
                to={getNavTarget(item.path)}
                onClick={(e) => handleNavClick(e, item.path)}
                className={`relative flex min-h-[3.25rem] min-w-0 flex-1 flex-col items-center justify-center gap-1 px-1 py-2 transition-colors ${
                  active ? 'text-primary-600' : 'text-slate-500'
                }`}
              >
                {active && (
                  <span className="absolute inset-x-3 top-0 h-0.5 rounded-full bg-primary-500" aria-hidden="true" />
                )}
                <span
                  className={`flex h-8 w-8 items-center justify-center rounded-full ${
                    active ? 'bg-primary-50' : ''
                  }`}
                >
                  <IconComponent
                    className={`h-5 w-5 shrink-0 ${active ? 'text-primary-600' : 'text-slate-400'}`}
                  />
                </span>
                <span className="max-w-full truncate text-center text-[11px] font-medium leading-none tracking-tight">
                  {item.shortLabel}
                </span>
              </Link>
            )
          })}
        </div>
      </nav>
      )}
    </div>
  )
}

const JobSeekerLayout = () => (
  <InterviewLeaveGuardProvider>
    <JobSeekerLayoutContent />
  </InterviewLeaveGuardProvider>
)

export default JobSeekerLayout
